import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useRef } from 'react';
import { useMutation } from 'react-query';
import produce from 'immer';

import { updateUserDetailQuery, useUserInfo } from '@/hooks';
import { Button, Modal } from '@/components';
import { request, toastError } from '@/utils';

import style from './index.module.css';

const { CommonModal } = Modal;

export const MemoEditModal = (props: {
  onClose: () => void;
  success?: Function;
  addr: string;
  prevMemo: string | undefined;
}) => {
  const { t } = useTranslation();

  const { onClose, addr, prevMemo, success } = props;
  const [input, setInput] = useState(prevMemo);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: userInfo } = useUserInfo(addr);
  const { mutateAsync, isLoading } = useMutation((comment: string) =>
    request.post<{
      is_success: boolean;
    }>('/user/update_memo', { target_id: addr, memo: comment }),
  );

  const updateUserQueryMemo = useCallback(
    (memo: string) => {
      userInfo &&
        updateUserDetailQuery(
          produce(userInfo, next => {
            if (next?.desc) {
              next.desc.user.memo = memo;
            }
            return next;
          }),
        );
    },
    [userInfo],
  );

  const handleConfirm = useCallback(async () => {
    try {
      const res = await mutateAsync(input!);
      if (!res?.is_success) return;
      updateUserQueryMemo(input!);
      success?.(input);
      onClose();
    } catch (error) {
      toastError(error as any);
    }
  }, [mutateAsync, input, updateUserQueryMemo, success, onClose]);

  const removeComment = useCallback(async () => {
    try {
      const res = await mutateAsync('');
      if (!res?.is_success) return;
      updateUserQueryMemo('');
      success?.('');
      onClose();
    } catch (error) {
      toastError(error as any);
    }
  }, [mutateAsync, updateUserQueryMemo, success, onClose]);

  const handleInputClick = useCallback(() => {
    inputRef?.current?.focus();
  }, []);

  return (
    <CommonModal
      onRequestClose={onClose}
      headerTitle={t('MemoEditModal.64f2fa')}
      size="normal"
      overlayClassName="memo-edit-modal">
      <div className={style.secondary}>{t('MemoEditModal.m4sp9p')}</div>
      <input
        ref={inputRef}
        onClick={handleInputClick}
        onChange={({ target }) => setInput(target.value)}
        value={input}
        className={style.input}
        placeholder="Address memo"
        maxLength={128}
      />
      <div className={style.footer}>
        <Button className={style.footerBtn} type="primary" ghost onClick={onClose}>
          {t('MemoEditModal.x9d2fu')}
        </Button>
        <Button
          loading={isLoading}
          className={style.footerBtn}
          type="primary"
          disabled={!input}
          onClick={handleConfirm}>
          {t('MemoEditModal.179d1nk')}
        </Button>
      </div>
      {prevMemo ? (
        <div className={style.subFooter}>
          <Button
            className={style.subFooterBtn}
            type="text"
            disabled={isLoading}
            block
            onClick={removeComment}>
            {t('MemoEditModal.lrgt9y')}
          </Button>
        </div>
      ) : null}
    </CommonModal>
  );
};

export default MemoEditModal;
